import { formatTimePrefix, isDaysPerWeekShape, isIntervalShape, type FhirTimingRepeat } from '@liza/fhir-utils';
import { format, parseISO } from 'date-fns';
import i18next from 'i18next';
import { DATE_FORMATS } from './date';

export type TimingRepeat = FhirTimingRepeat;

// ============================================================================
// Time sorting primitives (shared by Day page and Schedule page)
// ============================================================================

// The render-agnostic time primitives now live in `@liza/fhir-utils` (shared with
// patient-native); re-export them so existing `@liza/ui/utils/formatTiming` importers are unchanged.
export { WHEN_SORT_ORDER, formatTimePrefix, getTimeSortKey, timeToMinutes } from '@liza/fhir-utils';

const t = (key: string, options?: Record<string, unknown>): string => i18next.t(`common:${key}`, options);

type DayCode = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

const WEEKDAY_CODES: readonly DayCode[] = ['mon', 'tue', 'wed', 'thu', 'fri'];
const WEEKEND_CODES: readonly DayCode[] = ['sat', 'sun'];
const ALL_DAY_CODES: readonly DayCode[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_ORDER: Record<DayCode, number> = { mon: 0, tue: 1, wed: 2, thu: 3, fri: 4, sat: 5, sun: 6 };

const WHEN_LABEL_KEY: Record<string, string> = {
  'MORN.early': 'timeOfDay.earlyMorning',
  MORN: 'timeOfDay.morning',
  'MORN.late': 'timeOfDay.lateMorning',
  'AFT.early': 'timeOfDay.earlyAfternoon',
  AFT: 'timeOfDay.afternoon',
  'AFT.late': 'timeOfDay.lateAfternoon',
  'EVE.early': 'timeOfDay.earlyEvening',
  EVE: 'timeOfDay.evening',
  'EVE.late': 'timeOfDay.lateEvening',
  NIGHT: 'timeOfDay.night',
};

const isDayCode = (d: string): d is DayCode => d in DAY_ORDER;

const sortDays = (days: readonly DayCode[]): DayCode[] => [...days].sort((a, b) => DAY_ORDER[a] - DAY_ORDER[b]);

const hasAllDays = (days: readonly string[]): boolean =>
  days.length === 7 && ALL_DAY_CODES.every((d) => days.includes(d));

const hasWeekdays = (days: readonly string[]): boolean =>
  days.length === 5 && WEEKDAY_CODES.every((d) => days.includes(d));

const hasWeekend = (days: readonly string[]): boolean =>
  days.length === 2 && WEEKEND_CODES.every((d) => days.includes(d));

const capitalizeFirst = (s: string): string => (s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1));

const formatClockTime = (time: string): string => formatTimePrefix(time) ?? time;

const whenLabel = (w: string): string => t(WHEN_LABEL_KEY[w] ?? w);

// The i18next-cli extractor only picks up literal t() keys, so day-name lookups
// are spelled out per code via thunks instead of using a template literal.
const DAY_SHORT: Record<DayCode, () => string> = {
  mon: () => t('dayShort.mon'),
  tue: () => t('dayShort.tue'),
  wed: () => t('dayShort.wed'),
  thu: () => t('dayShort.thu'),
  fri: () => t('dayShort.fri'),
  sat: () => t('dayShort.sat'),
  sun: () => t('dayShort.sun'),
};

const DAY_LONG: Record<DayCode, () => string> = {
  mon: () => t('dayLong.mon'),
  tue: () => t('dayLong.tue'),
  wed: () => t('dayLong.wed'),
  thu: () => t('dayLong.thu'),
  fri: () => t('dayLong.fri'),
  sat: () => t('dayLong.sat'),
  sun: () => t('dayLong.sun'),
};

const DAY_PLURAL: Record<DayCode, () => string> = {
  mon: () => t('dayPlural.mon'),
  tue: () => t('dayPlural.tue'),
  wed: () => t('dayPlural.wed'),
  thu: () => t('dayPlural.thu'),
  fri: () => t('dayPlural.fri'),
  sat: () => t('dayPlural.sat'),
  sun: () => t('dayPlural.sun'),
};

const dayShortFor = (d: DayCode): string => DAY_SHORT[d]();
const dayLongFor = (d: DayCode): string => DAY_LONG[d]();
const dayPluralFor = (d: DayCode): string => DAY_PLURAL[d]();

const customWeeklyPrefix = (period: number): string =>
  period === 2 ? t('everyOtherWeek') : t('everyNWeeks', { count: period });

const monthlyPrefix = (period: number): string => {
  if (period === 1) return t('everyMonth');
  if (period === 2) return t('everyOtherMonth');
  return t('everyNMonths', { count: period });
};

const yearlyPrefix = (period: number): string => {
  if (period === 1) return t('everyYear');
  if (period === 2) return t('everyOtherYear');
  return t('everyNYears', { count: period });
};

const formatDayList = (days: readonly DayCode[]): string => days.map(dayShortFor).join(', ');

/**
 * An anchor-aligned interval ("every N days/weeks/months/years", period ≥ 2).
 * The anchor date determines *which* days/weeks/months the schedule lands on,
 * so the sentence always carries "starting <date>" — unlike other shapes,
 * where the start date is only shown while it is still in the future.
 * Anchoring is a sentence-level notion layered on the shared interval
 * predicate: a period-1 interval ("every week") reads unambiguously without
 * its anchor.
 */
const isAnchoredIntervalShape = (repeat: TimingRepeat): boolean => (repeat.period ?? 1) >= 2 && isIntervalShape(repeat);

/**
 * Builds the cadence portion of the human-readable label (everything before the
 * time-of-day, frequency, and start-date suffixes).
 *
 * Returns an empty string when the repeat has no cadence-bearing fields.
 */
// oxlint-disable-next-line complexity
const formatCadence = (repeat: TimingRepeat): string => {
  const dayOfWeek = repeat.dayOfWeek ?? [];
  const days = sortDays(dayOfWeek.filter(isDayCode));
  const periodUnit = repeat.periodUnit;
  const period = repeat.period ?? 1;

  if (days.length > 0) {
    if (periodUnit === 'wk' && period > 1) {
      // All 7 days under a custom weekly period collapses to just the period —
      // "every other week, daily" reads weirdly so the day list is suppressed.
      if (hasAllDays(days)) return customWeeklyPrefix(period);
      if (period === 2 && hasWeekend(days)) return t('everyOtherWeekend');
      if (period === 2 && days.length === 1) return t('everyOtherDayLong', { day: dayLongFor(days[0]!) });

      const prefix = customWeeklyPrefix(period);
      if (hasWeekdays(days)) return t('prefixOnWeekdays', { prefix });
      if (hasWeekend(days)) return t('prefixOnWeekends', { prefix });
      return t('prefixOnDays', { prefix, days: formatDayList(days) });
    }

    if (periodUnit === 'mo') {
      const prefix = monthlyPrefix(period);
      if (hasAllDays(days)) return prefix;
      if (hasWeekdays(days)) return t('prefixOnWeekdays', { prefix });
      if (hasWeekend(days)) return t('prefixOnWeekends', { prefix });
      return t('prefixOnDays', { prefix, days: formatDayList(days) });
    }

    // Default: weekly period=1 (or omitted).
    if (hasAllDays(days)) return t('daily');
    if (hasWeekdays(days)) return t('weekdays');
    if (hasWeekend(days)) return t('weekends');
    if (days.length === 1) return dayPluralFor(days[0]!);
    return formatDayList(days);
  }

  if (!periodUnit) return '';

  switch (periodUnit) {
    case 'h':
      return period === 1 ? t('everyHour') : t('everyNHours', { count: period });
    case 'd':
      if (period === 1) return t('daily');
      if (period === 2) return t('everyOtherDay');
      return t('everyNDays', { count: period });
    case 'wk':
      if (isDaysPerWeekShape(repeat)) return t('daysPerWeek', { count: repeat.frequency });
      if (period === 1) return t('everyWeek');
      if (period === 2) return t('everyOtherWeek');
      return t('everyNWeeks', { count: period });
    case 'mo':
      return monthlyPrefix(period);
    case 'a':
      return yearlyPrefix(period);
    default:
      return '';
  }
};

/**
 * The "starting <date>" suffix, or `undefined` when none applies. Anchored
 * intervals always carry their start date — the anchor sets the schedule's
 * phase (which days/weeks/months land) — with the year, since it is often in
 * the past. Every other shape shows the date only while it is still in the
 * future.
 */
const startingPhrase = (repeat: TimingRepeat): string | undefined => {
  if (!repeat.boundsPeriod?.start) return undefined;
  const startDate =
    repeat.boundsPeriod.start instanceof Date ? repeat.boundsPeriod.start : parseISO(String(repeat.boundsPeriod.start));
  if (isAnchoredIntervalShape(repeat)) {
    return t('startingDate', { date: format(startDate, DATE_FORMATS.DATE) });
  }
  const todayStr = format(new Date(), DATE_FORMATS.ISO_DATE);
  const startStr = format(startDate, DATE_FORMATS.ISO_DATE);
  if (startStr > todayStr) {
    return t('startingDate', { date: format(startDate, DATE_FORMATS.SHORT_DATE) });
  }
  return undefined;
};

/**
 * The schedule's ending suffix, or `undefined` when it runs indefinitely.
 * `count` ("ends after N times") wins over an end date when both are present —
 * it is the more specific end.
 */
const endingPhrase = (repeat: TimingRepeat): string | undefined => {
  // Mirror the engine (`lastOccurrenceDateByCount`): the days-per-week quota
  // never ends by count — its "times" are completions — so the sentence must
  // not promise an end the engine won't enforce.
  if (repeat.count && repeat.count > 0 && !isDaysPerWeekShape(repeat)) {
    return t('endsAfterCount', { count: repeat.count });
  }
  const end = repeat.boundsPeriod?.end;
  if (!end) return undefined;
  const endDate = end instanceof Date ? end : parseISO(String(end));
  return t('untilDate', { date: format(endDate, DATE_FORMATS.DATE) });
};

/**
 * When `cadence` is the plain daily word — or empty because the timing has no `periodUnit` at all
 * (legacy/malformed data with only `when`/`timeOfDay` set; `getScheduleLabel` treats a missing
 * `periodUnit` as daily too) — and there are 2+ items, replaces it with "Nx/day" (e.g. "2x/day",
 * "3x/day"). Otherwise returns `cadence` unchanged. A single time of day still reads as
 * "Daily (morning)".
 */
const dailyCadenceForCount = (repeat: TimingRepeat, cadence: string, count: number): string => {
  const isDaily = cadence === t('daily') || (cadence === '' && !repeat.periodUnit);
  return isDaily && count >= 2 ? t('timesPerDay', { count }) : cadence;
};

/**
 * Formats a FHIR TimingRepeat as a human-readable string following the
 * schedule-to-text rules (cadence → time-of-day → frequency → start date).
 */
export const formatTimingAsHumanReadable = (repeat: TimingRepeat): string => {
  const cadence = formatCadence(repeat);
  let result = cadence;

  if (repeat.timeOfDay && repeat.timeOfDay.length > 0) {
    const joined = repeat.timeOfDay.map(formatClockTime).join(', ');
    const label = dailyCadenceForCount(repeat, cadence, repeat.timeOfDay.length);
    result = label ? `${label} (${joined})` : capitalizeFirst(joined);
  } else if (repeat.when && repeat.when.length > 0) {
    const joined = repeat.when.map(whenLabel).join(', ');
    const label = dailyCadenceForCount(repeat, cadence, repeat.when.length);
    result = label ? `${label} (${joined})` : capitalizeFirst(joined);
  }

  const frequencyImpliedByWhen = repeat.when && repeat.frequency === repeat.when.length;
  if (repeat.frequency && repeat.frequency > 1 && !frequencyImpliedByWhen && !isDaysPerWeekShape(repeat)) {
    // "× N daily" disambiguates when the cadence isn't already "Daily"; the
    // cadence comparison is intentional even after time-of-day is appended,
    // since "Daily (morning) · 2×" still reads as twice-daily.
    const countMark = `${repeat.frequency}×`;
    const isDailyCadence = cadence === t('daily');
    if (result === '') {
      result = `${countMark} ${t('dailyLower')}`;
    } else if (isDailyCadence) {
      result = `${result} · ${countMark}`;
    } else {
      result = `${result} · ${countMark} ${t('dailyLower')}`;
    }
  }

  const starting = startingPhrase(repeat);
  if (starting) {
    result = result ? `${result}, ${starting}` : capitalizeFirst(starting);
  }

  const ending = endingPhrase(repeat);
  if (ending) {
    result = result ? `${result}, ${ending}` : capitalizeFirst(ending);
  }

  return result || t('recurring');
};
